/**
 * Lo que muestra el panel «Tu campaña» (29/10/2026) en todos los pasos: el nombre (y cambiarlo), de qué serie es si es una
 * repetición, a quién, el mensaje, cuándo sale, el costo máximo, lo que la frena y los avisos de la cuenta.
 *
 * - NO pide chequeos ni previas: cada chequeo anota en la auditoría. Lee lo que ya calcularon los pasos (el chequeo de
 *   «Revisar y mandar» o, antes, el de las variables) y lo que la campaña tiene guardado. Lo único que manda es el nombre,
 *   con `PATCH {nombre}` al confirmar la edición (no en cada tecla).
 * - A quién y el mensaje son lo GUARDADO, como «Vas a mandar»: es lo que va a salir. En «A quién», lo elegido que todavía
 *   no se guardó, con la cuenta de la pantalla y «Se guarda con «Siguiente»»; «Lo que la frena» sigue con eso (lo que
 *   falta después, o el motivo por el que «Siguiente» todavía no guarda). El chequeo, que es de la guardada, no se usa.
 * - Cuántos y el costo: lo último que se sabe (29/10/2026; antes, al entrar directo a «Cuándo», se perdían). El chequeo de
 *   esta carga de la página; si no hay, el último que guardó la campaña (el backend lo guarda con cada chequeo y lo borra
 *   con cada cambio: es de lo guardado); si tampoco, la cuenta de la audiencia que ya se pidió en esta carga (la de la
 *   pestaña, o el alcance de la segmentación) o el «hasta N» de la lista guardada. El costo sale sólo de un chequeo.
 */

import { useCallback } from "react";
import type { AudienciaActiva, CampanaDetalle, Chequeo, ConfigCuenta, CuentaPublica, EstadoPlantilla, ModoTransporte, Previa } from "../../types";
import type { PublicoResumen } from "../../types.base";
import { oficialApi } from "../../api";
import { loQueFrena, type FrenoDelAsistente, type PasoAsistente } from "../../logic/chequeo";
import { variablesSinClics } from "../../logic/clics";
import { esInutilizable, nombrePorDefecto } from "../../logic/asistente";
import { audienciaEnPalabras, textoPersonas, type CuandoDelEnvio } from "../../logic/vasAMandar";
import {
  avisosDeLaCuenta, comoGuardada, frenosDeLaCampana, mismaAudiencia, textoCuandoSale, type AudienciaEnPantalla, type RenglonDelPanel,
} from "../../logic/tuCampana";
import { usd } from "../../logic/formato";
import { textoRepeticionDe } from "../../logic/repeticion";
import { rutaCampana } from "../../logic/seguimiento";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import type { BorradorCampana } from "./useBorradorCampana";

/** Lo que el panel muestra, ya en palabras. */
export interface DatosTuCampana {
  /** el guardado o, sin borrador todavía, el que va a tener */
  nombre: string;
  /** sólo en una repetición: «Repetición 3 de «Campeones»», con el link a la original (su detalle tiene la serie) */
  repeticion: { texto: string; ruta: string } | null;
  /** `sinGuardar`: es lo elegido en «A quién», que se guarda con «Siguiente» */
  audiencia: { texto: string; cuantos: string | null; sinGuardar: boolean };
  /** la plantilla guardada (null sin plantilla): su nombre, su estado en Meta y una línea de la previa real, si ya hay */
  mensaje: { nombre: string; estado: EstadoPlantilla | null; previa: string | null } | null;
  cuando: string;
  /** el costo máximo del último chequeo (null sin chequeo: se calcula al revisar) */
  costo: string | null;
  frenos: RenglonDelPanel[];
  avisos: RenglonDelPanel[];
  /** la línea de la barra del celular: «612 personas · hasta USD 18» */
  corto: string;
}

export function useTuCampana({ paso, borrador, audiencia, chequeo, chequeoVars, previa, cuando, cuenta, config, modo }: {
  paso: PasoAsistente;
  borrador: BorradorCampana;
  /** lo elegido en «A quién» y, si «Siguiente» todavía no lo guarda, por qué (`useAudienciaCampana`) */
  audiencia: { enPantalla: AudienciaEnPantalla | null; motivo: string | null };
  /** el de «Revisar y mandar» (`useConfirmarCampana`) y el de las variables (`useVariablesCampana`) */
  chequeo: Chequeo | null; chequeoVars: Chequeo | null;
  previa: Previa | null;
  cuando: CuandoDelEnvio;
  cuenta: CuentaPublica | null; config: ConfigCuenta | null; modo: ModoTransporte | null;
}): { datos: DatosTuCampana; guardarNombre: ((n: string) => Promise<string>) | undefined } {
  const { campana, setCampana, detalles, plantillaId, estadoElegida, audiencias, publicos, camposValidos } = borrador;

  /** Cambia el nombre guardado. Devuelve el error, o "" si salió. */
  const guardarNombre = useCallback(async (n: string): Promise<string> => {
    const nombre = n.trim();
    if (!campana) return "";
    if (!nombre) return "Poné un nombre.";
    if (nombre === campana.nombre) return "";
    try {
      const l = leerRespuesta<CampanaDetalle>(await oficialApi.editarCampana(campana.id, { nombre }));
      if (!l.ok) return l.error || textoError(l);
      setCampana(l.data);
      return "";
    } catch (e) {
      return textoError(leerError(e));
    }
  }, [campana, setCampana]);

  const { enPantalla } = audiencia;
  const esLaGuardada = !!enPantalla && mismaAudiencia(enPantalla.entrada, campana?.audiencia);
  // lo elegido en «A quién» que «Siguiente» todavía no guardó (en los otros pasos, lo guardado: es lo que va a salir)
  const sinGuardar = paso === "audiencia" && enPantalla && !esLaGuardada ? enPantalla : null;
  // el chequeo es de la audiencia guardada: con otra elegida, no dice nada de ella
  const ultimo = sinGuardar ? null : chequeo ?? chequeoVars;
  // cuántos y el costo, también del último chequeo que guardó la campaña: un F5 no los pierde
  const conNumeros = sinGuardar ? null : ultimo ?? campana?.chequeo ?? null;

  const guardada = campana?.plantilla ?? null;
  const detalleGuardada = guardada ? detalles[guardada.id] : undefined;
  const numeros = variablesSinClics(detalleGuardada?.variables, detalleGuardada?.botones).map((v) => v.numero);
  const freno: FrenoDelAsistente | null = sinGuardar && audiencia.motivo
    ? { hasta: "audiencia", motivo: audiencia.motivo, arreglo: "audiencia" }
    : loQueFrena({ ...(campana ?? { plantilla: null, mapeo: {} }), audiencia: sinGuardar ? comoGuardada(sinGuardar.entrada) : campana?.audiencia ?? null }, numeros, {
      plantillaInutilizable: !!detalleGuardada && esInutilizable(detalleGuardada, modo), plantillaArchivada: !!detalleGuardada?.archivada, camposValidos,
    });

  const pueden = sinGuardar ? sinGuardar.pueden : conNumeros ? conNumeros.envio : esLaGuardada ? enPantalla?.pueden ?? null : null;
  const cuantos = pueden !== null ? textoPersonas(pueden) : sinGuardar ? null : hastaDeLaBase(campana, publicos);
  const costo = conNumeros ? usd(conNumeros.costo.clienteMaximoUSD) : null;
  const datos: DatosTuCampana = {
    nombre: campana?.nombre || nombrePorDefecto(new Date()),
    repeticion: campana?.repeticionDe ? { texto: textoRepeticionDe(campana.repeticionDe), ruta: rutaCampana(campana.repeticionDe.campanaId) } : null,
    audiencia: {
      texto: sinGuardar ? sinGuardar.texto : audienciaEnPalabras(campana, { grupos: gruposDe(campana, audiencias), chequeo: ultimo }).origen,
      cuantos,
      sinGuardar: !!sinGuardar,
    },
    mensaje: guardada ? {
      nombre: guardada.nombreVisible,
      // el de la elegida, si es la guardada: lo actualiza el estado en vivo
      estado: (plantillaId === guardada.id ? estadoElegida : undefined) ?? guardada.estado ?? null,
      previa: previa?.ejemplos?.[0]?.texto || null,
    } : null,
    // la franja no depende de la audiencia: con «Ahora» y la franja cerrada, cuándo abre (como «Vas a mandar»)
    cuando: textoCuandoSale(cuando, chequeo ?? chequeoVars),
    costo,
    frenos: frenosDeLaCampana(freno, ultimo),
    avisos: avisosDeLaCuenta(cuenta, config, modo),
    corto: [cuantos, costo ? `hasta ${costo}` : null].filter(Boolean).join(" · "),
  };
  return { datos, guardarNombre: campana ? guardarNombre : undefined };
}

/** Los grupos del análisis de Activa guardado, para nombrar los elegidos. */
function gruposDe(c: CampanaDetalle | null, audiencias: AudienciaActiva[] | null) {
  return audiencias?.find((a) => a.id === c?.audiencia?.campanaActivaId)?.grupos;
}

/** Sin otra cuenta, con un público de la base: cuántos pueden recibir según su último conteo («hasta»). */
function hastaDeLaBase(c: CampanaDetalle | null, publicos: PublicoResumen[] | null): string | null {
  if (c?.audiencia?.tipo !== "base") return null;
  const n = publicos?.find((p) => p.id === c.audiencia?.publicoId)?.conteo?.puedenRecibir;
  return typeof n === "number" ? `hasta ${textoPersonas(n)}` : null;
}
